import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./merchant-nav.css";
import {
  RefreshIconV1,
  MenuIconV1,
  CoinsIconV1,
  KeyIconV1,
  InfoIconV1,
  LogOutIconV1,
  WalletIconV1,
} from "images/image-component";
import BrandLogoV1 from "components/brand-logo-v1/BrandLogoV1";
import RolePill from "components/role-pill/RolePill";
import SettingsDrawer, {
  DrawerItem,
  DrawerSection,
} from "components/settings-drawer/SettingsDrawer";
import { useRefreshMerchant } from "hooks/useRefreshMerchant";
import { useAuth } from "auth";
import { usePaymentAccount } from "features/payment-account/usePaymentAccount";
import PaymentAccountModals from "features/payment-account/PaymentAccountModals";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import { useTokenSupport } from "features/tokens/useTokenSupport";
import { getNetworkConfig, NETWORK_NAME, NetworkType } from "utils/network";
import { tokenLabel } from "utils/tokens";

interface Props {
  networkType?: NetworkType;
}

function MerchantNav({ networkType }: Props) {
  const navigate = useNavigate();
  const network = getNetworkConfig(networkType);
  const { isRefreshing, handleRefresh } = useRefreshMerchant();
  const { signOut } = useAuth();
  const paymentAccount = usePaymentAccount();
  const {
    tokens,
    selected: selectedToken,
    label: tokenSymbol,
    param: tokenAddress,
    canSwitch,
    select: selectToken,
  } = useSettlementToken();
  // The portal chose the token; only the deploy knows whether its registry holds it. `undefined`
  // means "not answered yet" and deliberately shows nothing — an unreachable backend is not
  // evidence that a token is unusable.
  const { data: tokenSupport } = useTokenSupport(tokenAddress);
  const tokenIssue =
    tokenSupport?.supported === false
      ? `${tokenSymbol} is not enabled on this network`
      : tokenSupport?.supported && tokenSupport.enabled === false
        ? `${tokenSymbol} is paused for new payments`
        : undefined;
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleLogout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  const tokenDrawerItems: DrawerItem[] = tokens.map((token) => ({
    label: tokenLabel(token),
    icon: <CoinsIconV1 />,
    selected: token.address === selectedToken?.address,
    onClick: () => selectToken(token.address),
  }));

  const settingsSections: DrawerSection[] = [
    {
      label: "Preferences",
      // Absent, not disabled, when the network offers only one token.
      items: canSwitch
        ? [
            {
              label: "Network token",
              icon: <CoinsIconV1 />,
              value: tokenSymbol,
              hint: tokenIssue,
              badge: tokenIssue ? "!" : undefined,
              submenu: {
                title: "Network token",
                sections: [{ items: tokenDrawerItems }],
              },
            },
          ]
        : [],
    },
    {
      label: "Account",
      items: [
        {
          label: "Payment account",
          icon: <WalletIconV1 />,
          onClick: () => paymentAccount.handleManagePaymentAccount(),
        },
        {
          label: "Change password",
          icon: <KeyIconV1 />,
          onClick: () => navigate("/update-password"),
        },
      ],
    },
    {
      label: "Help and settings",
      items: [
        {
          label: "About",
          icon: <InfoIconV1 />,
          external: true,
          onClick: () => window.open("https://kea-valley.com/", "_blank"),
        },
        {
          label: "Logout",
          icon: <LogOutIconV1 />,
          variant: "danger",
          onClick: handleLogout,
        },
      ],
    },
  ];

  return (
    <>
      <header className="gp-topbar">
        <div className="gp-topbar__brand">
          <BrandLogoV1 />
          <RolePill label="Merchant" variant="brand" />
        </div>

        <div className="gp-nav-actions">
          <button
            className={`refresh_mini-btn${isRefreshing ? " refresh_mini-btn--spinning" : ""}`}
            aria-label="Refresh data"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshIconV1 size={16} />
          </button>
          {tokenSymbol && (
            <span
              className={`gp-token-label${tokenIssue ? " gp-token-label--warn" : ""}`}
              title={tokenIssue ?? `Amounts shown in ${tokenSymbol}`}
            >
              {tokenSymbol}
            </span>
          )}
          <div className={`gp-chip-network gp-chip-network--${network.value}`}>
            {/* Separate span so the narrow-viewport rule can drop the prefix and
                keep just "Mainnet" / "Testnet". */}
            <span className="gp-chip-network__prefix">{NETWORK_NAME}</span>
            {network.shortLabel}
          </div>
          <button
            className="gp-nav-menu-btn"
            aria-label="Settings"
            aria-expanded={settingsOpen}
            aria-haspopup="dialog"
            onClick={() => setSettingsOpen(true)}
          >
            <MenuIconV1 />
          </button>
        </div>
      </header>

      <SettingsDrawer
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        sections={settingsSections}
      />

      <PaymentAccountModals {...paymentAccount} />
    </>
  );
}

export default MerchantNav;
